import { useQuery } from '@tanstack/react-query';

import { hasValue } from 'utils/hasValue';

import type { IntegrationBusinessUnit } from 'api';
import {
  getGetIntegrationBusinessUnitsQueryKey,
  getIntegrationBusinessUnits
} from 'api';

import { useOrganisationId } from 'hooks/context';

const FETCH_ALL_PAGE_SIZE = '200';

const fetchAllBusinessUnits = async (organisationId: number) => {
  const all: IntegrationBusinessUnit[] = [];
  let cursorPrev: string | undefined;
  let hasNextPage = true;
  while (hasNextPage) {
    const response = await getIntegrationBusinessUnits(organisationId, {
      page_size: FETCH_ALL_PAGE_SIZE,
      ...(hasValue(cursorPrev) ? { cursor_prev: cursorPrev } : {})
    });
    all.push(...response.data);
    hasNextPage =
      response.meta.has_next_page === true &&
      hasValue(response.meta.cursor_next);
    cursorPrev = hasNextPage ? String(response.meta.cursor_next) : undefined;
  }
  return all;
};

export const useBusinessUnits = () => {
  const organisationId = useOrganisationId<number>();
  const { data, isLoading, isError } = useQuery({
    queryKey: [
      ...getGetIntegrationBusinessUnitsQueryKey(organisationId),
      'all'
    ],
    queryFn: () => fetchAllBusinessUnits(organisationId),
    enabled: !!organisationId
  });

  const businessUnits = data ?? [];
  const defaultUuid = businessUnits.find((unit) => unit.default)?.uuid;

  return { businessUnits, defaultUuid, isLoading, isError };
};
